import * as THREE from 'three';
import { G, addMesh, mat } from './models.js';

/**
 * Market stalls. Built in local space with the counter along x (three tiles), customers on +z and
 * the keeper standing behind at −z. The front shutter is hinged at the front beam: folded back on
 * top of the awning by day, swung down over the counter at night when the keeper has gone home.
 */
export interface Stall { group: THREE.Group; shutter: THREE.Group; lamp: THREE.Vector3; open: number }

const AWNING: Record<string, [number, number]> = {
  fish: [0x3a7fa0, 0xeee2c0], food: [0xb8483a, 0xf2e4c4], smith: [0x4a4d53, 0xc48a3f], general: [0x3a8a5a, 0xeee2c0],
  wood: [0x6b8a3a, 0xe8d8a8], ore: [0x8a5a3a, 0xd8c8a8], camp: [0x5a6a8a, 0xe0d8c0], trade: [0x7a4a8a, 0xe8d8b0],
};

export function buildStall(style: string): Stall {
  const g = new THREE.Group();
  const [c1, c2] = AWNING[style] ?? AWNING.general;
  const wood = 0x7a5634, dark = 0x5a3d24;
  // counter and posts
  addMesh(g, G.box(2.9, 0.82, 0.62), 0x8a6238, 0, 0.41, 0.05);
  addMesh(g, G.box(3.0, 0.07, 0.74), 0xb08a5a, 0, 0.85, 0.06);
  for (let i = 0; i < 6; i++) addMesh(g, G.box(0.03, 0.6, 0.02), dark, -1.25 + i * 0.5, 0.42, 0.37);
  for (const x of [-1.42, 1.42]) { addMesh(g, G.box(0.11, 2.35, 0.11), wood, x, 1.17, -0.55); addMesh(g, G.box(0.11, 2.0, 0.11), wood, x, 1.0, 0.38); }
  // striped, sloping awning
  // the awning only overhangs the counter, so the keeper standing behind stays in view
  const awn = new THREE.Group(); awn.position.set(0, 2.12, 0.3); awn.rotation.x = 0.3; g.add(awn);
  for (let i = 0; i < 7; i++) addMesh(awn, G.box(3.15 / 7 + 0.005, 0.05, 0.82), i % 2 ? c2 : c1, -1.575 + (i + 0.5) * (3.15 / 7), 0, 0);
  for (let i = 0; i < 7; i++) addMesh(awn, G.box(3.15 / 7 - 0.04, 0.16, 0.03), i % 2 ? c2 : c1, -1.575 + (i + 0.5) * (3.15 / 7), -0.08, 0.42);
  for (const x of [-1.42, 1.42]) addMesh(g, G.box(0.08, 0.06, 0.55), wood, x, 2.28, -0.33);
  // the shutter: hinged at the front beam
  addMesh(g, G.box(3.0, 0.09, 0.09), dark, 0, 1.96, 0.38);
  const shutter = new THREE.Group(); shutter.position.set(0, 1.94, 0.42); g.add(shutter);
  addMesh(shutter, G.box(2.86, 1.02, 0.05), 0x9a7448, 0, -0.52, 0);
  for (const y of [-0.2, -0.85]) addMesh(shutter, G.box(2.86, 0.06, 0.07), dark, 0, y, 0.01);
  // goods on the counter, by trade
  const top = 0.89, r = (a: number, b: number) => a + Math.random() * (b - a);
  const goods = new THREE.Group(); g.add(goods);
  const on = (geo: THREE.BufferGeometry, col: number, x: number, y: number, z: number, opts?: THREE.MeshLambertMaterialParameters) => addMesh(goods, geo, col, x, top + y, z, opts);
  switch (style) {
    case 'fish': {
      on(G.box(1.5, 0.05, 0.5), 0xcfe8f0, -0.35, 0.02, 0.05, { emissive: 0x203038 });
      for (let i = 0; i < 6; i++) { const f = on(G.oct(0.1), [0xb8c4c8, 0x9ab0b8, 0xc8b090][i % 3], -0.95 + i * 0.24, 0.07, 0.05 + (i % 2 ? 0.1 : -0.1)); f.scale.set(0.55, 0.5, 1.9); f.rotation.y = 0.3 + (i % 2) * 0.4; }
      for (let i = 0; i < 3; i++) { const f = addMesh(g, G.oct(0.1), 0x9ab0b8, -1.0 + i * 0.4, 1.65, 0.36); f.scale.set(0.5, 2.0, 0.6); addMesh(g, G.box(0.01, 0.2, 0.01), 0x3a3a38, -1.0 + i * 0.4, 1.86, 0.36); }
      addMesh(g, G.cyl(0.26, 0.24, 0.62, 8), 0x8a6238, 1.0, top + 0.31, 0.05); addMesh(g, G.cyl(0.27, 0.27, 0.05, 8), 0x3a3a38, 1.0, top + 0.5, 0.05);
      break;
    }
    case 'food': {
      for (let i = 0; i < 4; i++) { const l = on(G.dod(0.12), 0xc8964a, -1.1 + i * 0.26, 0.08, 0.0); l.scale.set(1.5, 0.75, 0.95); }
      on(G.cyl(0.18, 0.2, 0.1, 10), 0xd8a860, 0.05, 0.05, 0.05); on(G.cyl(0.15, 0.15, 0.02, 10), 0xa84a3a, 0.05, 0.11, 0.05);
      on(G.cyl(0.16, 0.13, 0.26, 8), 0x3a3a3e, 0.6, 0.13, 0.0); on(G.box(0.38, 0.12, 0.26), 0x9a7448, 1.05, 0.06, 0.05);
      for (let i = 0; i < 5; i++) on(G.ico(0.05), 0xb890c0, 0.95 + (i % 3) * 0.09, 0.15, 0.0 + (i > 2 ? 0.08 : 0));
      for (let i = 0; i < 4; i++) { const s = addMesh(g, G.cyl(0.035, 0.035, 0.22, 5), 0x9a4a32, -0.9 + i * 0.12, 1.7, 0.36); s.rotation.z = 0.15; }
      break;
    }
    case 'smith': {
      for (let i = 0; i < 3; i++) { const b = on(G.box(0.05, 0.02, 0.75), 0xa3aab1, -1.0 + i * 0.18, 0.02, 0.05); b.rotation.y = 1.3; on(G.box(0.18, 0.03, 0.04), 0x5a3a20, -1.0 + i * 0.18 - 0.36, 0.03, 0.12).rotation.y = 1.3; }
      on(G.box(0.3, 0.2, 0.3), 0xa3aab1, 0.25, 0.1, 0.05); on(G.box(0.3, 0.06, 0.3), 0x8a9096, 0.25, 0.21, 0.05);
      on(G.cyl(0.22, 0.22, 0.04, 10), 0xc48a3f, 0.85, 0.2, 0.05).rotation.x = 1.3;
      const rack = new THREE.Group(); rack.position.set(1.75, 0, -0.1); g.add(rack);
      addMesh(rack, G.box(0.1, 1.2, 0.5), wood, 0, 0.6, 0);
      for (let i = 0; i < 3; i++) addMesh(rack, G.box(0.04, 0.9, 0.05), 0xa3aab1, 0.08, 0.75, -0.16 + i * 0.16);
      break;
    }
    case 'wood': {
      for (let i = 0; i < 2; i++) { const a = on(G.box(0.05, 0.05, 0.6), 0x6b4a2b, -0.8 + i * 0.4, 0.03, 0.05); a.rotation.y = 0.4; on(G.box(0.18, 0.05, 0.14), 0xa3aab1, -0.8 + i * 0.4 + 0.1, 0.04, 0.3).rotation.y = 0.4; }
      for (let i = 0; i < 3; i++) on(G.box(1.0, 0.05, 0.16), 0xc8a070, 0.6, 0.03 + i * 0.05, 0.05 - i * 0.02);
      const pile = new THREE.Group(); pile.position.set(1.85, 0, -0.1); g.add(pile);
      for (let i = 0; i < 6; i++) { const l = addMesh(pile, G.cyl(0.12, 0.12, 0.7, 7), 0x8a6238, (i % 3) * 0.24 - 0.24, 0.12 + Math.floor(i / 3) * 0.22, 0); l.rotation.x = Math.PI / 2; }
      break;
    }
    case 'ore': {
      const cols = [0xe0863c, 0xd4dee3, 0xb0523a, 0x9a958a];
      for (let i = 0; i < 9; i++) on(G.dod(r(0.07, 0.11)), cols[i % 4], -1.1 + i * 0.27, 0.07, r(-0.1, 0.15), { emissive: cols[i % 4], emissiveIntensity: 0.08 });
      const pick = addMesh(g, G.box(0.05, 1.0, 0.05), 0x6b4a2b, 1.6, 0.5, 0.25); pick.rotation.z = 0.35; addMesh(g, G.box(0.5, 0.06, 0.06), 0xa3aab1, 1.78, 0.96, 0.25).rotation.z = 0.35;
      break;
    }
    case 'camp': {
      on(G.box(0.5, 0.02, 0.36), 0xe8dcb8, -0.7, 0.02, 0.05).rotation.y = 0.2;
      on(G.cyl(0.04, 0.04, 0.36, 6), 0xd8c8a0, -0.15, 0.04, 0.05).rotation.z = Math.PI / 2;
      on(G.cyl(0.14, 0.17, 0.2, 8), 0x3a3a3e, 0.45, 0.1, 0.05); on(G.cyl(0.03, 0.03, 0.12, 5), 0x3a3a3e, 0.58, 0.18, 0.05).rotation.z = 0.9;
      for (let i = 0; i < 2; i++) { const rod = addMesh(g, G.cyl(0.015, 0.025, 2.0, 4), 0x9b7b4a, 1.6 + i * 0.12, 1.0, -0.3); rod.rotation.z = -0.25 + i * 0.12; }
      break;
    }
    case 'trade': {
      for (let i = 0; i < 6; i++) on(G.box(0.24, 0.07, 0.11), i < 3 ? 0xc48a3f : 0xa3aab1, -1.0 + (i % 3) * 0.26, 0.04 + (i % 2) * 0.07, i < 3 ? -0.05 : 0.12);
      on(G.box(0.04, 0.34, 0.04), dark, 0.4, 0.17, 0.05); on(G.box(0.5, 0.03, 0.03), dark, 0.4, 0.34, 0.05);
      for (const x of [0.17, 0.63]) on(G.cyl(0.1, 0.1, 0.02, 8), 0xc48a3f, x, 0.2, 0.05);
      on(G.box(0.4, 0.34, 0.4), 0xa07a44, 1.05, 0.17, 0.0);
      break;
    }
    default: {
      on(G.box(0.4, 0.32, 0.34), 0xa07a44, -0.95, 0.16, 0.0); on(G.box(0.4, 0.08, 0.34), 0x8a6238, -0.95, 0.36, 0.0);
      for (let i = 0; i < 3; i++) on(G.cyl(0.07, 0.08, 0.2, 7), [0x6a8ab0, 0xc8b070, 0x8ab08a][i], -0.4 + i * 0.2, 0.1, 0.05);
      on(G.dod(0.18), 0xd8c8a0, 0.45, 0.13, 0.05).scale.set(1, 0.8, 0.9);
      const rope = new THREE.Mesh(new THREE.TorusGeometry(0.13, 0.04, 4, 10), mat(0xc8a878)); rope.rotation.x = Math.PI / 2; rope.position.set(0.95, top + 0.04, 0.05); goods.add(rope);
      addMesh(g, G.box(0.4, 0.4, 0.4), 0xa07a44, 1.75, 0.2, 0.1); addMesh(g, G.box(0.34, 0.34, 0.34), 0x9a7448, 1.72, 0.57, 0.05);
    }
  }
  // a lantern on the front post for the evenings
  addMesh(g, G.box(0.3, 0.04, 0.04), dark, 1.55, 1.9, 0.38);
  const lantern = new THREE.Mesh(G.box(0.12, 0.17, 0.12), new THREE.MeshBasicMaterial({ color: 0xffc070 })); lantern.position.set(1.66, 1.72, 0.38); g.add(lantern);
  addMesh(g, G.cone(0.1, 0.08, 4), dark, 1.66, 1.85, 0.38).rotation.y = Math.PI / 4;
  g.traverse((o: THREE.Object3D) => { if ((o as THREE.Mesh).isMesh) { o.castShadow = true; o.receiveShadow = true; } });
  return { group: g, shutter, lamp: new THREE.Vector3(1.66, 1.72, 0.38), open: 1 };
}
